import { cancel, put, retry, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
	setComponentLoading,
	setComponentErrors,
	setKs4HasBeenChecked,
	setErrorCode,
} from '../../ks5PerformanceMeasures/singleSchool/slice';
import { sagaFlowConfigurations } from '../../../../../../constants';
import { getFacilitatingSubjectsDataBySchoolId } from '../../../../../../api/groupsAPI';
import { getActiveChildSchool, getGroupAccessId } from 'features/app/redux/context';
import { setMainGridDataSuccess } from '../../ks5PerformanceMeasures/singleSchool/slice';
import { getGroupAccessRecords } from 'features/groups/setup/redux';
import { getKs4HasBeenChecked } from '../../ks5PerformanceMeasures/singleSchool/selectors';
import { getGridDataForGridId } from '../../../../../../sharedSelectors';
import { translate } from '../../../../../../utils/locale';

/**
 * Get the facilitating subjects data
 * @returns {SagaIterator<void>}
 */
export function* getFacilitatingSubjectsFlow(): SagaIterator<void> {
	//get the school id
	const schoolId = yield select(getActiveChildSchool);

	if (!schoolId) {
		//bail out of the saga
		yield cancel();
	}

	//get the data from the store
	const initialData = yield select(
		getGridDataForGridId(
			'matSingleSchoolPerformanceMeasures',
			`0-${schoolId.childSchoolId},AchievingAABInTwoFacilitatingSubjects`
		)
	);
	//clear the errors for this component
	yield put(setComponentErrors({ component: 'facilitatingSubjects', value: undefined }));

	//set the component loading state to true
	yield put(setComponentLoading({ component: 'facilitatingSubjects', value: true }));

	//get the group access id
	const groupAccessId = yield select(getGroupAccessId);

	//if the group access id is 0, bail out of the saga
	if (groupAccessId === 0) {
		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'facilitatingSubjects',
				value: translate('groups.ks5Pm.common.NO_ACCESS_ID') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//get the access records (available key stages)
	const accessRecords = yield select(getGroupAccessRecords);

	//check the access record for the the current group access id for KS4
	const isKs4 = accessRecords?.find((x) => x.id === groupAccessId).examLevel === 32;

	//get the state of KS4 from the store
	const ks4HasBeenChecked = yield select(getKs4HasBeenChecked);

	//if the access record is for KS4, check to see if the state of KS4 has already been set
	if (isKs4) {
		if (!ks4HasBeenChecked) {
			//set the state of KS4 to true
			yield put(setKs4HasBeenChecked(true));
		}
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));

		//bail out of the saga
		yield cancel();
	}
	//clear the state of KS4
	yield put(setKs4HasBeenChecked(false));

	//check to see if the data is already in the store
	if (initialData !== undefined) {
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//get the data from the api
	const facilitatingSubjectsData = yield retry(
		sagaFlowConfigurations.apiRetry,
		sagaFlowConfigurations.milliSeconds,
		getFacilitatingSubjectsDataBySchoolId,
		groupAccessId,
		schoolId.childSchoolId
	);

	//if there is an error, set the errors for this component
	if (facilitatingSubjectsData.hasError) {
		//if the error code is 204, set the errors for this component
		if (facilitatingSubjectsData.errors[0].errorCode === 204) {
			yield put(
				setComponentErrors({
					component: 'facilitatingSubjects',
					value: ' ',
				})
			);

			//set the errors for this component
			yield put(
				setErrorCode({
					component: 'facilitatingSubjects',
					value: 204,
				})
			);
			//set the component loading state to false
			yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));

			yield cancel();
		}

		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'facilitatingSubjects',
				value: translate('groups.ks5Pm.common.MESSAGE') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//set the data in the store
	yield put(setMainGridDataSuccess(facilitatingSubjectsData.responseObject));

	//set the component loading state to false
	yield put(setComponentLoading({ component: 'facilitatingSubjects', value: false }));

	//bail out of the saga
	yield cancel();
}
